import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { uploadImageData } from "./storageDataService";

import {
  getPlatform,
  isNativeFirestorePlatform,
  removeNativeSnapshotListener,
  extractNativeDocuments,
} from "./firestorePlatform";


const OFFERS_COLLECTION = "angebote";
const OFFERS_REFERENCE = OFFERS_COLLECTION;

const normalizeOffer = (id, data) => {
  const o = { id, ...(data || {}) };

  if (Number.isFinite(Number(o.lat)) && Number.isFinite(Number(o.lng))) {
    o.lat = Number(o.lat);
    o.lng = Number(o.lng);
    return o;
  }

  if (Number.isFinite(Number(o.latitude)) && Number.isFinite(Number(o.longitude))) {
    o.lat = Number(o.latitude);
    o.lng = Number(o.longitude);
    return o;
  }

  if (o.location) {
    const l = o.location;
    const lat = l?.lat ?? l?.latitude;
    const lng = l?.lng ?? l?.longitude;

    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
      o.lat = Number(lat);
      o.lng = Number(lng);
      return o;
    }
  }

  if (Array.isArray(o.coords) && o.coords.length >= 2) {
  const lat = Number(o.coords[0]);
  const lng = Number(o.coords[1]);

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    o.lat = lat;
    o.lng = lng;
    return o;
  }
}

if (o.coords && typeof o.coords === "object") {
  const lat = o.coords.lat ?? o.coords.latitude;
  const lng = o.coords.lng ?? o.coords.longitude;

  if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
    o.lat = Number(lat);
    o.lng = Number(lng);
    return o;
  }
}

  const possibleGeo =
    o.geo || o.position || o.locationGeo || o.geoPoint || o.geopoint || null;

  if (possibleGeo && typeof possibleGeo === "object") {
    const lat = possibleGeo.latitude;
    const lng = possibleGeo.longitude;

    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
      o.lat = Number(lat);
      o.lng = Number(lng);
      return o;
    }
  }

  return o;
};

const extractDocId = (docItem, index) => {
  return (
    docItem?.id ||
    docItem?.documentId ||
    docItem?.snapshot?.id ||
    docItem?.document?.id ||
    (typeof docItem?.reference === "string" ? docItem.reference.split("/").pop() : null) ||
    `native-offer-${index}`
  );
};

const extractDocData = (docItem) => {
  const candidates = [
    docItem?.data,
    docItem?.snapshot?.data,
    docItem?.document?.data,
    docItem?.value,
    docItem?.fields,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      return candidate;
    }
  }

  return {};
};

const mapNativeDocuments = (payload) => {
  const docs = extractNativeDocuments(payload);

  return docs.map((docItem, index) => {
    const id = extractDocId(docItem, index);
    const data = extractDocData(docItem);
    return normalizeOffer(id, data);
  });
};

const getOffersViaWebSdk = async () => {
  console.log("🟢 getOffersViaWebSdk START");

  const snapshot = await getDocs(collection(db, OFFERS_COLLECTION));
  const offers = snapshot.docs.map((d) => normalizeOffer(d.id, d.data()));

  console.log("🟢 getOffersViaWebSdk RESULT", {
    count: offers.length,
    offers,
  });

  return offers;
};

const getOffersViaNativeFirestore = async () => {
  console.log("🟢 getOffersViaNativeFirestore START", {
    platform: getPlatform(),
    reference: OFFERS_REFERENCE,
  });

  const result = await FirebaseFirestore.getCollection({
    reference: OFFERS_REFERENCE,
  });

  const offers = mapNativeDocuments(result);

  console.log("🟢 getOffersViaNativeFirestore RESULT", {
    raw: result,
    extractedDocuments: extractNativeDocuments(result),
    count: offers.length,
    offers,
  });

  return offers;
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));


export const getOffers = async () => {
  try {
    if (isNativeFirestorePlatform()) {
      console.log("🟢 Offers data source: Native Firestore");
      return await getOffersViaNativeFirestore();
    }

    console.log("🟢 Offers data source: Firestore Web SDK");
    return await getOffersViaWebSdk();
  } catch (error) {
    console.error("🟢 getOffers FAILED", {
      platform: getPlatform(),
      name: error?.name || null,
      message: error?.message || null,
      stack: error?.stack || null,
      full: error,
    });
    throw error;
  }
};

export const subscribeToOffers = async (onData, onError) => {
  console.log("🟢 subscribeToOffers START", {
    platform: getPlatform(),
    reference: OFFERS_REFERENCE,
  });

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.addCollectionSnapshotListener(
      {
        reference: OFFERS_REFERENCE,
      },
      (event, error) => {
        if (error) {
          console.error("🟢 Native offers listener ERROR", error);
          if (typeof onError === "function") onError(error);
          return;
        }

        const offers = mapNativeDocuments(event);

        console.log("🟢 Native offers listener EVENT", {
          raw: event,
          extractedDocuments: extractNativeDocuments(event),
          count: offers.length,
          offers,
        });

        if (typeof onData === "function") onData(offers);
      }
    );

   const listenerId = result?.callbackId || result;

return async () => {
  await removeNativeSnapshotListener(listenerId);
};
  }

  const unsubscribe = onSnapshot(
    collection(db, OFFERS_COLLECTION),
    (snapshot) => {
      const offers = snapshot.docs.map((d) => normalizeOffer(d.id, d.data()));

      console.log("🟢 Web offers listener EVENT", {
        count: offers.length,
        offers,
      });

      if (typeof onData === "function") onData(offers);
    },
    (error) => {
      console.error("🟢 Web offers listener ERROR", error);
      if (typeof onError === "function") onError(error);
    }
  );

  return () => {
    unsubscribe();
    console.log("🟢 Web offers listener removed");
  };
};

export const createOfferData = async (payload) => {
  if (isNativeFirestorePlatform()) {
    const id = `angebot_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    await FirebaseFirestore.setDocument({
      reference: `${OFFERS_REFERENCE}/${id}`,
      data: {
        ...payload,
        createdAtClient: new Date().toISOString(),
        updatedAtClient: new Date().toISOString(),
      },
      merge: false,
    });

    return id;
  }

  const refDoc = await addDoc(collection(db, OFFERS_COLLECTION), {
    ...payload,
    createdAt: Timestamp.now(),
  });

  return refDoc.id;
};

export const updateOfferData = async (offerId, payload) => {
  if (!offerId) throw new Error("updateOfferData: offerId fehlt");

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.setDocument({
      reference: `${OFFERS_REFERENCE}/${offerId}`,
      data: {
        ...payload,
        updatedAtClient: new Date().toISOString(),
      },
      merge: true,
    });

    return;
  }

  await updateDoc(doc(db, OFFERS_COLLECTION, String(offerId)), {
    ...payload,
    updatedAt: Timestamp.now(),
  });
};

export const getOfferByIdData = async (offerId) => {
  if (!offerId) return null;

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getDocument({
      reference: `${OFFERS_REFERENCE}/${offerId}`,
    });

    const data =
      result?.snapshot?.data ||
      result?.data ||
      result?.document?.data ||
      result?.value ||
      result?.fields ||
      null;

    if (!data || Object.keys(data).length === 0) return null;

    return normalizeOffer(String(offerId), data);
  }

  const snap = await getDoc(doc(db, OFFERS_COLLECTION, String(offerId)));
  if (!snap.exists()) return null;

  return normalizeOffer(snap.id, snap.data());
};
export const uploadOfferImagesData = async (items, userId) => {
  const input = Array.isArray(items) ? items : [];
  const urls = [];

  for (let i = 0; i < input.length; i++) {
    const item = input[i];
    if (!item) continue;

    if (item.type === "existing" && item.url) {
      urls.push(item.url);
      continue;
    }

    const file = item.file;

    if (!(file instanceof File) && !(file instanceof Blob)) {
      continue;
    }

    const safeName = String(
      file.name || `angebot-${Date.now()}-${i}.jpg`
    ).replace(/\s+/g, "_");

    const path = `angebote/${userId}/${Date.now()}_${i}_${safeName}`;

    console.log("🟢 Angebotsbild Upload starte:", {
      path,
      name: file.name,
      type: file.type,
      size: file.size,
      nativePath: file.nativePath || null,
    });

    const url = await uploadImageData(file, path);

    urls.push(url);
  }

  return urls;
};